import { firestore } from '@src/api/Firebase/Firebase';
import { collection, Timestamp } from 'firebase/firestore';
import { ShoppingCartProduct } from '@api/ShoppingCart/ShoppingCart';
import { Customer } from '@api/Customers/Customer';
import { UserType } from '@api/Users/User';
import { PromotionalCode } from '@api/PromotionalCodes/PromotionalCode';
import { SalesChannel } from '@api/SalesChannels/SalesChannel';
import { TableKiosk, TableKioskType } from '@api/TablesKiosks/TableKiosk';
import { OrderCreatedWith, OrderPayment } from '@api/Orders/OrderPayments';

export const orderStatusTranslations: Record<string, string> = {
    WaitingForPayment: 'Pendiente de pago',
    Preparing: 'En Preparación',
    Paid: 'Pagado',
    Completed: 'Completado',
    Cancelled: 'Cancelado',
    Ready: 'Listo',
    Route: 'En Ruta'
};

export const paymentMethodsTranslations: Record<string, string> = {
    CashMXN: 'Efectivo',
    CashUSD: 'Dolares',
    CreditCard: 'Tarjeta de crédito',
    BankTransfer: 'Transferencia'
};

export enum DeliveryType {
    Delivery = 'Delivery',
    Pickup = 'Pickup'
}

export enum OrderStatus {
    WaitingForPayment = 'WaitingForPayment',
    Preparing = 'Preparing',
    Paid = 'Paid',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
    Ready = 'Ready',
    Route = 'Route'
}

/**
 * Estados en los que una orden COMPROMETE puntos en la cartera del cliente.
 * El canje (pointsAmount) se descuenta del saldo desde que la orden está pagada
 * o en curso, y solo se regresa si la orden se cancela o se borra. Si aquí
 * faltara un estado intermedio (ej. Ready), la orden "desaparecería" del
 * recálculo al pasar por él y el cliente podría volver a gastar esos puntos.
 * WaitingForPayment queda fuera a propósito: el checkout web crea órdenes en
 * ese estado que pueden abandonarse.
 * La GANANCIA de puntos se otorga únicamente cuando la orden se completa.
 */
export const POINTS_LEDGER_ORDER_STATUSES = [
    OrderStatus.Paid,
    OrderStatus.Preparing,
    OrderStatus.Ready,
    OrderStatus.Route,
    OrderStatus.Completed
];

export enum PaymentMethods {
    CreditCard = 'CreditCard',
    Cash = 'Cash',
    BankTransfer = 'BankTransfer',
    Mixed = 'Mixed'
}

export interface DeliveryAddress {
    address: string;
    googlePlaceId: string;
    location: {
        lat: number;
        lng: number;
    };
}

export interface StripePaymentInfo {
    paymentIntentId: string;
    paymentMethodId: string;
    status: string;
    clientSecret: string | null;
    lastPaymentError?: string;
    amount: number;
    currency: string;
    created: Timestamp;
    lastUpdate: Timestamp;
}

export interface Order {
    id?: string;
    shortId: string;
    status: OrderStatus;
    products: ShoppingCartProduct[];
    customer: Customer;
    branchOfficeId: string;
    collaboratorId: string;
    collaboratorData?: UserType;
    promotionalCode?: PromotionalCode;
    salesChannel?: SalesChannel;
    note: string;
    subtotal: number;
    promotion: number;
    shippingCost: number;
    total: number;
    pointsAmount: number;
    // Propina (PLAN-ESPEJO-POS-Y-PROPINA, D2): NO es venta — nunca sumarla a
    // subtotal/total ni a puntos; los montos de `payments[]` SÍ la incluyen
    // (el efectivo del cajón la contiene y el corte ciego cuadra solo).
    tipAmount?: number;
    /**
     * PAGOS — la ÚNICA fuente de cómo se pagó. Se lee con `getOrderPayments` /
     * `getOrderPaidBy` (`@api/Orders/OrderPayments`) y se arma con
     * `buildOrderPayments`. Los 3 escalares viejos (`cashAmount`,
     * `creditCardAmount`, `bankTransferAmount`) se retiraron en la F6 del plan de
     * pagos (2026-10): las órdenes viejas los conservan como fósil y nadie los lee.
     * Invariante: Σ payments[].amount === total + (tipAmount || 0).
     */
    payments?: OrderPayment[];
    /** Con qué app y qué build se creó la orden. Lo escribe quien la crea; no se recalcula. */
    createdWith?: OrderCreatedWith;
    date: any;
    lastUpdate: any;
    duration: number;
    /**
     * Rastro del motor de inventario (F5) — lo escribe el SERVER, nunca el cliente.
     *
     * `consumption` es la explosión CONGELADA de la venta (insumo por insumo, en
     * unidades base y en NEGATIVO porque son salidas). Se persiste aunque el
     * descuento esté apagado en la sucursal: registrar el consumo y mover el stock
     * son cosas distintas. De aquí lee "Ventas por insumo" el consumo de órdenes v2
     * — su receta no vive en la línea, así que sin esto no hay forma de desglosarlo.
     */
    inventory?: {
        committed: boolean;
        movementId?: string;
        consumption?: { inputId: string; inputName: string; quantity: number; unit: string }[];
        warnings?: string[];
        reason?: string;
        reverted?: boolean;
        revertMovementId?: string;
        date?: Timestamp | Date;
    };
    // New data
    deliveryType?: DeliveryType;
    deliveryAddress?: DeliveryAddress;
    pickupDate?: any;
    routeUrl?: string;
    uberRouteUrl?: string;
    uberDeliveryId?: string;
    /**
     * Último estado que reportó Uber (`delivered`, `canceled`, `returned`…), tal
     * como lo escribe el webhook del server. **Es rastro de auditoría, no la
     * fuente del estado del pedido**: ése es `status`, y cuando Uber entrega,
     * el webhook lo cierra completo (puntos, bono de bebidas y pase de Wallet
     * incluidos). Sirve para distinguir un pedido que Uber entregó de uno que
     * cerró una persona.
     */
    uberDeliveryStatus?: string;
    /** Cuándo avisó Uber que el repartidor entregó. */
    uberDeliveredAt?: Timestamp;
    uberDisplayId?: string;
    stripe?: StripePaymentInfo;
    // Tables
    tableKiosk?: TableKiosk;
    // Cuenta de mesa: pedidos originales de los que salió esta consolidada (se
    // borran para no duplicar la venta, así que su rastro vive aquí). Presente
    // SOLO en órdenes consolidadas — también sirve de candado anti doble cierre.
    consolidatedFrom?: {
        /** Id de la original (desde el 14-oct): el server lo usa para no descontarla dos veces. */
        orderId?: string;
        shortId: string;
        date: Timestamp;
        total: number;
        customerName: string;
    }[];
    // Reservations
    isReservation?: boolean;
    reservationDate?: Timestamp;
}

/**
 * ¿Es un pedido de MESA (cuenta abierta que se cierra y se cobra)?
 *
 * `tableKiosk` guarda mesas Y kioscos, pero durante mucho tiempo solo las mesas
 * lo escribían, así que media docena de flujos daban por hecho "si trae
 * tableKiosk es una mesa": liberar la mesa al completar/purgar, marcarla
 * ORDER_OPEN al crear la orden y los botones "Pedido listo"/"Cerrar mesa y
 * pagar". Desde que los pedidos de kiosco también guardan su kiosco
 * (krispis-web, 2026-08-17) esa suposición es falsa y hay que preguntar por el
 * TIPO.
 *
 * Un `type` ausente se trata como mesa a propósito: las órdenes históricas con
 * `tableKiosk` son todas de mesa, y asumir kiosco les quitaría el cierre de cuenta.
 */
export const isTableOrder = (order?: Order): boolean =>
    !!order?.tableKiosk?.id && order.tableKiosk.type !== TableKioskType.KIOSK;

export const ORDERS_COLLECTION = 'orders';

export const ordersRef = collection(firestore, ORDERS_COLLECTION);
